import { defaultLocale } from '~/locale/defaults';
import {
    ADDRESS_1_REGEX,
    ADDRESS_2_REGEX,
    CITY_REGEX,
    CONCESSION_NUMBER_REGEX,
    LOT_NUMBER_REGEX,
    PLAN_NUMBER_REGEX,
    POSTAL_CODE_ON_REGEX,
    POSTAL_CODE_REGEX,
    STREET_NUMBER_REGEX,
    TOWNSHIP_REGEX,
    UNIT_NUMBER_REGEX,
    STREET_SUFFIX_REGEX,
} from './regex';
import { Address, DriverLicense, HealthCard } from '~/models/profile';
import { Locale } from '~/locale/types';

export const DEFAULT_LANGUAGE = defaultLocale;
export const FEEDBACK_CALLOUT_DISMISSED_KEY = 'cxp-feedback-callout-dismissed';

export const HealthCardTypeOptions = [
    { id: 'photoHealthCard', text: 'Photo health card', value: 'PhotoHealthCard' },
    {
        id: 'redWhiteHealthCard',
        text: 'Red and white health card',
        value: 'RedWhiteHealthCard',
    },
];

export const YesNoOptions = [
    { id: 'yes', text: 'optionYes', value: 'yes' },
    { id: 'no', text: 'optionNo', value: 'no' },
];

export const SexDesignation = [
    { id: 'male', text: 'male', value: 'Male' },
    { id: 'female', text: 'female', value: 'Female' },
    { id: 'genderNeutral', text: 'neutral', value: 'Gender Neutral' },
];

export const SEX_MAPPINGS: { [key: string]: string } = {
    male: 'personalDetails.maleGender',
    female: 'personalDetails.femaleGender',
    'gender neutral': 'personalDetails.neutralGender',
};

export const OTHER_PRONOUN = 'other';

export const GenderPronouns = [
    { id: 'hePronoun', text: 'he-him', value: 'He/Him', hintId: '', hintText: '' },
    { id: 'shePronoun', text: 'she-her', value: 'She/Her', hintId: '', hintText: '' },
    { id: 'theyPronoun', text: 'they-them', value: 'They/Them', hintId: '', hintText: '' },
    { id: 'namePronoun', text: 'use-own-name', value: 'Use Own Name', hintId: '', hintText: '' },
    {
        id: 'otherPronoun',
        text: 'other',
        value: 'other',
        hintId: '',
        hintText: '',
    },
];

export const Provinces = (language = DEFAULT_LANGUAGE) =>
    language?.toUpperCase() === DEFAULT_LANGUAGE.toUpperCase()
        ? [
              { text: 'Select', value: '' },
              { text: 'Alberta', value: 'AB' },
              { text: 'British Columbia', value: 'BC' },
              { text: 'Manitoba', value: 'MB' },
              { text: 'New Brunswick', value: 'NB' },
              { text: 'Newfoundland and Labrador', value: 'NL' },
              { text: 'Northwest Territories', value: 'NT' },
              { text: 'Nunavut', value: 'NU' },
              { text: 'Nova Scotia', value: 'NS' },
              { text: 'Ontario', value: 'ON' },
              { text: 'Prince Edward Island', value: 'PE' },
              { text: 'Quebec', value: 'QC' },
              { text: 'Saskatchewan', value: 'SK' },
              { text: 'Yukon', value: 'YT' },
          ]
        : [
              { text: 'Select', value: '' },
              { text: "L'Alberta", value: 'AB' },
              { text: 'La Colombie-Britannique', value: 'BC' },
              { text: 'Le Manitoba', value: 'MB' },
              { text: 'Le Nouveau-Brunswick', value: 'NB' },
              { text: 'La Terre-Neuve-et-Labrador', value: 'NL' },
              { text: 'Les Territoires du Nord-Ouest', value: 'NT' },
              { text: 'Le Nunavut', value: 'NU' },
              { text: 'La Nouvelle-Écosse', value: 'NS' },
              { text: "L'Ontario", value: 'ON' },
              { text: 'Île-du-Prince-Édouard', value: 'PE' },
              { text: 'Le Québec', value: 'QC' },
              { text: 'La Saskatchewan', value: 'SK' },
              { text: 'Le Yukon (Territoire)', value: 'YT' },
          ];

export const EthnicOrigins = [
    { text: 'Select', value: '' },
    { text: 'Canadian', value: 'Canadian' },
    { text: 'Indigenous', value: 'Indigenous' },
    { text: 'East Indian', value: 'East Indian' },
    { text: 'English', value: 'English' },
    { text: 'Scottish', value: 'Scottish' },
    { text: 'Irish', value: 'Irish' },
    { text: 'German', value: 'German' },
    { text: 'French', value: 'French' },
    { text: 'Iranian', value: 'Iranian' },
    { text: 'Other', value: 'Other' },
];

export const IDTypes = (language = DEFAULT_LANGUAGE, controlType: 'Radio' | 'Select' = 'Select') =>
    language?.toUpperCase() === DEFAULT_LANGUAGE.toUpperCase()
        ? [
              ...(controlType === 'Select' ? [{ text: 'Select', value: '' }] : []),
              { text: "Ontario driver's licence", value: 'DriversLicense' },
              { text: 'Ontario photo card', value: 'PhotoCard' },
          ]
        : [
              ...(controlType === 'Select' ? [{ text: 'Sélectionner', value: '' }] : []),
              { text: 'Permis de conduire', value: 'DriversLicense' },
              { text: 'Carte-photo de l’Ontario', value: 'PhotoCard' },
          ];

export const IDTypesDetails = (language = DEFAULT_LANGUAGE) =>
    language?.toUpperCase() === DEFAULT_LANGUAGE.toUpperCase()
        ? [
              { text: '', value: '' },
              { text: "Ontario driver's licence", value: 'DriversLicense' },
              { text: 'Ontario photo card', value: 'PhotoCard' },
          ]
        : [
              { text: '', value: '' },
              { text: 'Permis de conduire de l’Ontario', value: 'DriversLicense' },
              { text: 'Carte-photo de l’Ontario', value: 'PhotoCard' },
          ];

export const PATTERN_ERRORS = {
    ADDRESS_1: { message: 'CXPAD006', value: ADDRESS_1_REGEX },
    ADDRESS_2: { message: 'CXPAD007', value: ADDRESS_2_REGEX },
    STREET_SUFFIX: { message: 'CXPAD022', value: STREET_SUFFIX_REGEX },
    CITY: { message: 'CXPAD008', value: CITY_REGEX },
    POSTAL_CODE: { message: 'CXPAD005', value: POSTAL_CODE_REGEX },
    POSTAL_CODE_ON: { message: 'CXPAD005', value: POSTAL_CODE_ON_REGEX },
    UNIT_NUMBER: { message: 'CXPFE01715', value: UNIT_NUMBER_REGEX },
    LOT_NUMBER: { message: 'CXPFE01711', value: LOT_NUMBER_REGEX },
    PLAN_NUMBER: { message: 'CXPFE01713', value: PLAN_NUMBER_REGEX },
    CONCESSION_NUMBER: { message: 'CXPFE01712', value: CONCESSION_NUMBER_REGEX },
    TOWNSHIP: { message: 'CXPFE01714', value: TOWNSHIP_REGEX },
    STREET_NUMBER: { message: 'CXPFE0162', value: STREET_NUMBER_REGEX },
};

export const DEFAULT_COUNTRY_CODE = 'CA';

export const REMINDER_TYPES = {
    EMAIL: 'EMAIL',
    SMS: 'SMS',
    VOICE: 'VOICE',
};

export const enum FLAG {
    Yes = 'Y',
    No = 'N',
}

export const UnitTypes = (language = DEFAULT_LANGUAGE) => {
    return language?.toUpperCase() === DEFAULT_LANGUAGE.toUpperCase()
        ? [
              { text: '', value: '' },
              { text: 'Apartment', value: 'APT' },
              { text: 'Suite', value: 'SUITE' },
              { text: 'Unit', value: 'UNIT' },
          ]
        : [
              { text: '', value: '' },
              { text: 'Appartment', value: 'APT' },
              { text: 'Suite', value: 'SUITE' },
              { text: 'Unité', value: 'UNIT' },
          ];
};

export const StreetTypes = [
    { text: '', value: '' },
    { text: 'Abbey', value: 'ABBEY' },
    { text: 'Acres', value: 'ACRES' },
    { text: 'Allée', value: 'ALLéE' },
    { text: 'Alley', value: 'ALLEY' },
    { text: 'Autoroute', value: 'AUT' },
    { text: 'Avenue', value: 'AVE' },
    { text: 'Avenue', value: 'AV' },
    { text: 'Bay', value: 'BAY' },
    { text: 'Beach', value: 'BEACH' },
    { text: 'Bend', value: 'BEND' },
    { text: 'Boulevard', value: 'BLVD' },
    { text: 'Boulevard', value: 'BOUL' },
    { text: 'By-pass', value: 'BYPASS' },
    { text: 'Byway', value: 'BYWAY' },
    { text: 'Campus', value: 'CAMPUS' },
    { text: 'Cape', value: 'CAPE' },
    { text: 'Carré', value: 'CAR' },
    { text: 'Carrefour', value: 'CARREF' },
    { text: 'Centre', value: 'CTR' },
    { text: 'Centre', value: 'C' },
    { text: 'Cercle', value: 'CERCLE' },
    { text: 'Chase', value: 'CHASE' },
    { text: 'Chemin', value: 'CH' },
    { text: 'Circle', value: 'CIR' },
    { text: 'Circuit', value: 'CIRCT' },
    { text: 'Close', value: 'CLOSE' },
    { text: 'Common', value: 'COMMON' },
    { text: 'Concession', value: 'CONC' },
    { text: 'Corners', value: 'CRNRS' },
    { text: 'Côte', value: 'CÔTE' },
    { text: 'Cour', value: 'COUR' },
    { text: 'Cours', value: 'COURS' },
    { text: 'Court', value: 'CRT' },
    { text: 'Cove', value: 'COVE' },
    { text: 'Crescent', value: 'CRES' },
    { text: 'Croissant', value: 'CROIS' },
    { text: 'Crossing', value: 'CROSS' },
    { text: 'Cul-de-sac', value: 'CDS' },
    { text: 'Dale', value: 'DALE' },
    { text: 'Dell', value: 'DELL' },
    { text: 'Diversion', value: 'DIVERS' },
    { text: 'Downs', value: 'DOWNS' },
    { text: 'Drive', value: 'DR' },
    { text: 'échangeur', value: 'éCH' },
    { text: 'End', value: 'END' },
    { text: 'Esplanade', value: 'ESPL' },
    { text: 'Estates', value: 'ESTATE' },
    { text: 'Expressway', value: 'EXPY' },
    { text: 'Extension', value: 'EXTEN' },
    { text: 'Farm', value: 'FARM' },
    { text: 'Field', value: 'FIELD' },
    { text: 'Forest', value: 'FOREST' },
    { text: 'Freeway', value: 'FWY' },
    { text: 'Front', value: 'FRONT' },
    { text: 'Gardens', value: 'GDNS' },
    { text: 'Gate', value: 'GATE' },
    { text: 'Glade', value: 'GLADE' },
    { text: 'Glen', value: 'GLEN' },
    { text: 'Green', value: 'GREEN' },
    { text: 'Grounds', value: 'GRNDS' },
    { text: 'Grove', value: 'GROVE' },
    { text: 'Harbour', value: 'HARBR' },
    { text: 'Heath', value: 'HEATH' },
    { text: 'Heights', value: 'HTS' },
    { text: 'Highlands', value: 'HGHLDS' },
    { text: 'Highway', value: 'HWY' },
    { text: 'Hill', value: 'HILL' },
    { text: 'Hollow', value: 'HOLLOW' },
    { text: 'Île', value: 'ÎLE' },
    { text: 'Impasse', value: 'IMP' },
    { text: 'Inlet', value: 'INLET' },
    { text: 'Island', value: 'ISLAND' },
    { text: 'Key', value: 'KEY' },
    { text: 'Knoll', value: 'KNOLL' },
    { text: 'Landing', value: 'LANDNG' },
    { text: 'Lane', value: 'LANE' },
    { text: 'Limits', value: 'LMTS' },
    { text: 'Line', value: 'LINE' },
    { text: 'Link', value: 'LINK' },
    { text: 'Lookout', value: 'LKOUT' },
    { text: 'Loop', value: 'LOOP' },
    { text: 'Mall', value: 'MALL' },
    { text: 'Manor', value: 'MANOR' },
    { text: 'Maze', value: 'MAZE' },
    { text: 'Meadow', value: 'MEADOW' },
    { text: 'Mews', value: 'MEWS' },
    { text: 'Montée', value: 'MONTéE' },
    { text: 'Moor', value: 'MOOR' },
    { text: 'Mount', value: 'MOUNT' },
    { text: 'Mountain', value: 'MTN' },
    { text: 'Orchard', value: 'ORCH' },
    { text: 'Parade', value: 'PARADE' },
    { text: 'Parc', value: 'PARC' },
    { text: 'Park', value: 'PK' },
    { text: 'Parkway', value: 'PKY' },
    { text: 'Passage', value: 'PASS' },
    { text: 'Path', value: 'PATH' },
    { text: 'Pathway', value: 'PTWAY' },
    { text: 'Pines', value: 'PINES' },
    { text: 'Place', value: 'PL' },
    { text: 'Place', value: 'PLACE' },
    { text: 'Plateau', value: 'PLAT' },
    { text: 'Plaza', value: 'PLAZA' },
    { text: 'Point', value: 'PT' },
    { text: 'Pointe', value: 'POINTE' },
    { text: 'Port', value: 'PORT' },
    { text: 'Private', value: 'PVT' },
    { text: 'Promenade', value: 'PROM' },
    { text: 'Quai', value: 'QUAI' },
    { text: 'Quay', value: 'QUAY' },
    { text: 'Ramp', value: 'RAMP' },
    { text: 'Rang', value: 'RANG' },
    { text: 'Range', value: 'RG' },
    { text: 'Ridge', value: 'RIDGE' },
    { text: 'Rise', value: 'RISE' },
    { text: 'Road', value: 'RD' },
    { text: 'Rond-point', value: 'RDPT' },
    { text: 'Route', value: 'RTE' },
    { text: 'Row', value: 'ROW' },
    { text: 'Rue', value: 'RUE' },
    { text: 'Ruelle', value: 'RLE' },
    { text: 'Run', value: 'RUN' },
    { text: 'Sentier', value: 'SENT' },
    { text: 'Square', value: 'SQ' },
    { text: 'Street', value: 'ST' },
    { text: 'Subdivision', value: 'SUBDIV' },
    { text: 'Terrace', value: 'TERR' },
    { text: 'Terrasse', value: 'TSSE' },
    { text: 'Thicket', value: 'THICK' },
    { text: 'Towers', value: 'TOWERS' },
    { text: 'Townline', value: 'TLINE' },
    { text: 'Trail', value: 'TRAIL' },
    { text: 'Turnabout', value: 'TRNABT' },
    { text: 'Vale', value: 'VALE' },
    { text: 'Via', value: 'VIA' },
    { text: 'View', value: 'VIEW' },
    { text: 'Village', value: 'VILLGE' },
    { text: 'Villas', value: 'VILLAS' },
    { text: 'Vista', value: 'VISTA' },
    { text: 'Voie', value: 'VOIE' },
    { text: 'Walk', value: 'WALK' },
    { text: 'Way', value: 'WAY' },
    { text: 'Wharf', value: 'WHARF' },
    { text: 'Wood', value: 'WOOD' },
    { text: 'Wynd', value: 'WYND' },
];

export const StreetDirections = (language = DEFAULT_LANGUAGE) =>
    language?.toUpperCase() === DEFAULT_LANGUAGE.toUpperCase()
        ? [
              { text: '', value: '' },
              { text: 'North', value: 'N' },
              { text: 'South', value: 'S' },
              { text: 'East', value: 'E' },
              { text: 'West', value: 'W' },
              { text: 'North-east', value: 'NE' },
              { text: 'North-west', value: 'NW' },
              { text: 'South-east', value: 'SE' },
              { text: 'South-west', value: 'SW' },
          ]
        : [
              { text: '', value: '' },
              { text: 'nord', value: 'N' },
              { text: 'sud', value: 'S' },
              { text: 'est', value: 'E' },
              { text: 'ouest', value: 'W' },
              { text: 'nord-est', value: 'NE' },
              { text: 'nord-ouest', value: 'NW' },
              { text: 'sud-est', value: 'SE' },
              { text: 'sud-ouest', value: 'SW' },
          ];

export const RouteTypes = (language = DEFAULT_LANGUAGE) =>
    language?.toUpperCase() === DEFAULT_LANGUAGE.toUpperCase()
        ? [
              { text: '', value: '' },
              { text: 'Rural route', value: 'RR' },
              { text: 'Suburban service', value: 'SS' },
              { text: 'Mobile route', value: 'MR' },
              { text: 'General delivery', value: 'GD' },
          ]
        : [
              { text: '', value: '' },
              { text: 'Route Rurale', value: 'RR' },
              { text: 'Service de banlieue', value: 'SS' },
              { text: 'Itinéraire mobile', value: 'MR' },
              { text: 'Poste restante', value: 'GD' },
          ];

export const LotTypes = (language = DEFAULT_LANGUAGE) =>
    language?.toUpperCase() === DEFAULT_LANGUAGE.toUpperCase()
        ? [
              { text: '', value: '' },
              { text: 'LOT', value: 'L' },
              { text: 'PART LOT', value: 'P' },
              { text: 'BLOCK', value: 'B' },
              { text: 'PART BLOCK', value: 'K' },
          ]
        : [
              { text: '', value: '' },
              { text: 'Parcelle', value: 'L' },
              { text: 'parc', value: 'P' },
              { text: 'Bloc', value: 'B' },
              { text: 'Bloc de pièces', value: 'K' },
          ];

export const StreetNumberSuffix = [
    { text: '', value: '' },
    { text: '1/4', value: '1' },
    { text: '1/2', value: '2' },
    { text: '3/4', value: '3' },
    { text: 'A', value: 'A' },
    { text: 'B', value: 'B' },
    { text: 'C', value: 'C' },
    { text: 'D', value: 'D' },
    { text: 'E', value: 'E' },
    { text: 'G', value: 'G' },
    { text: 'H', value: 'H' },
    { text: 'I', value: 'I' },
    { text: 'J', value: 'J' },
    { text: 'K', value: 'K' },
    { text: 'L', value: 'L' },
    { text: 'M', value: 'M' },
    { text: 'N', value: 'N' },
    { text: 'O', value: 'O' },
    { text: 'P', value: 'P' },
    { text: 'Q', value: 'Q' },
    { text: 'R', value: 'R' },
    { text: 'S', value: 'S' },
    { text: 'T', value: 'T' },
    { text: 'U', value: 'U' },
    { text: 'V', value: 'V' },
    { text: 'W', value: 'W' },
    { text: 'X', value: 'X' },
    { text: 'Y', value: 'Y' },
    { text: 'Z', value: 'Z' },
];

export const PERSONAL_DETAILS_DATE_FORMAT = 'yyyy-MM-dd';
export const PERSONAL_DETAILS_MAX_AGE = 120;
export const PERSONAL_DETAILS_MIN_AGE = 16;
export const APP_HEADER_MAX_INBOX_MESSAGES = 3;
export const INBOX_PAGE_SIZE = 10;
export const RENEWAL_REMINDERS = 'RenewalReminders';
export const IS_STARRED = '/isStarred';
export const IS_READ = '/isRead';
export const REPLACE = 'replace';
export const enum CALLOUT_TYPE {
    ASIDE = 'aside',
    CALLOUT = 'callout',
}

export const HARDSTOP_ERROR_CODES = [
    'CXP0102',
    'CXP0103',
    'CXP011',
    'CXP012',
    'CXP013',
    'CXP016',
    'CXP020',
    'CXP022',
    'CXP023',
    'CXP025',
    'CXP026',
    'CXP029',
    'CXP030',
    'CXP031',
    'CXP032',
    'CXP033',
    'CXP034',
    'CXPFE031',
    'CXPFE032',
    'CXPFE035',
    'CXPIAC001',
    'CXPIAC011',
    'CXPIAC012',
    'CXPIAC013',
    'CXPIAC014',
    'CXPIAC015',
    'CXPIAC016',
    'CXPIAC017',
    'CXPIAC018',
    'CXPIAC019',
    'CXPIAC034',
    'CXPIAC035',
    'CXPIAC044',
];

export const mtoRetryErrorCodes = [
    'CXPR345',
    'CXPR346',
    'CXPR347',
    'CXPR348',
    'CXPR373',
    'CXPR374',
    'CXPR375',
    'CXPR376',
    'CXPR420',
    'CXPR421',
    'CXPR422',
    'CXPR423',
];

export const mtoEsdiInquiryErrorCodesForPageErrors = [
    ...mtoRetryErrorCodes,
    'CXPR404',
    'CXPR405',
    'CXPR406',
    'CXPR417',
];

export const mtoLicenseUnfitHardStopErrors = [
    'CXPR351',
    'CXPR352',
    'CXPR353',
    'CXPR354',
    'CXPR355',
    'CXPR356',
    'CXPR357',
    'CXPR358',
    'CXPR359',
    'CXPR360',
    'CXPR361',
    'CXPR362',
];

export const mtoLicenseConditionNotMetHardStopErrors = [
    'CXPR349',
    'CXPR350',
    'CXPR363',
    'CXPR364',
    'CXPR365',
    'CXPR366',
    'CXPR367',
    'CXPR368',
    'CXPR369',
    'CXPR370',
    'CXPR371',
    'CXPR372',
];
export const mtoHardStopErrors = [
    ...mtoLicenseUnfitHardStopErrors,
    ...mtoLicenseConditionNotMetHardStopErrors,
];
export const mohOhcrErrorCodesForPageErrors = []; // TODO: check to delete this if not used
export const profileErrorCodes: string[] = [
    'CXPPD041',
    'CXPPD042',
    'CXPPD043',
    'CXPPD044',
    'CXPPD045',
];

export const specialSuspensionsCodes = new Set([
    '12',
    '13',
    '18',
    '19',
    '25',
    '91',
    '92',
    '93',
    '95',
    'A1',
    'A5',
    'A7',
]);

export const PCL_DELAY_TIME_MS = 2000;
export const driverFineCodes = ['CXPLPR106', 'CXPLPR105'];
export const invalidInsuranceCodes = ['CXPLPR107', 'CXPLPR108'];
export const renewInPersonCodes = ['CXPLPR103'];

export const STATUS_CODE_QUERY_PARAM = {
    400: '&sts=400',
    100: '&sts=100',
};

export const ERROR_CODE_PARAM = '&code=';

export const SOURCE_PARAM = '&src=';

export const LOCALE_PARAM = '&sglocale=';

export const LICENCE_PLATE_RENEWAL_ELIGIBILITY_THRESHOLD_DAYS = 90;

export enum Flow {
    IAC = 'IAC', // address change
    DLR = 'DLR', // driver licence renewal
    HCR = 'HCR', // healthcard renewal
    LPR = 'LPR', // licenceplate renewal
    OPCR = 'OPCR', // ontario photo card renewal
    DLV = 'DLV', // driver licence verification
    OPCV = 'OPCV', // ontario photo card verification
    HCV = 'HCV', // health card renewal with driver licence or ontario photo card verification
    DLHCV = 'DLHCV', // driver licence and health card verification
    IACV = 'IACV', // address change verification
    PF = 'PF', // pay fines
    DR3 = 'DR3', // driver record 3-year
    VA = 'VA', // vehicle abstract uncertified
    EAB = 'EAB', // enterprise appointment booking
    SP = 'SP', // special permit
    CHC = 'CHC', // child health care
    APP = 'APP', // accessible parking permit
    OTRIS = 'OTRIS', // ontario tax payer rebate
    CHC_TRACK = 'CHC_TRACK', // child health care renewal status tracking
    NFHL = 'NFHL', // new fishing and hunting license
    ISSUER_PORTAL = 'ISSUER_PORTAL', // NFHL Issuer Portal
    CCEY = 'CCEY',
    FAVR = 'FAVR',
    GLP = 'GLP', // graphic licence plates
    PLP = 'PLP', // personalized licence plates
    PUB_SGR = 'PUB-SGR', // publication service-guarantee-refund
    EHOCM = 'EHOCM', // file consumre complaint
    ODR = 'ODR', // organ donor registry
    OER = 'OER',
    VECON = 'VECON', // adult change of name
    HSO = 'HSO', // healthySmilesOntarioProgram
}

// This is used if the Azure dev-ops library or runtime configuration does not provide a specific version.
// Updated from '1.0' to '3.0' to reflect changes in the NOC specification, as per the requirements from Bill 194.
export const DEFAULT_NOC_VERSION_CODE = '3.0';

export const FormBuilderAppUrlMap = {
    [Flow.APP]: process.env.APP_ENDPOINT || '',
    [`${Flow.APP}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.APP.toLowerCase()}`,
    [Flow.CHC]: process.env.CHC_ENDPOINT || '',
    [`${Flow.CHC}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.CHC.toLowerCase()}`,
    [Flow.CHC_TRACK]: process.env.CHC_TRACK_ENDPOINT || '',
    [Flow.OTRIS]: process.env.OTRIS_ENDPOINT || '',
    [Flow.PF]: process.env.PF_ENDPOINT || '',
    [Flow.GLP]: process.env.GLP_ENDPOINT || '',
    [`${Flow.GLP}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.GLP.toLowerCase()}`,
    [Flow.PLP]: process.env.PLP_ENDPOINT || '',
    [`${Flow.PLP}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.PLP.toLowerCase()}`,
    [Flow.PUB_SGR]: process.env.PUB_SGR_ENDPOINT || '',
    [`${Flow.PUB_SGR}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.PUB_SGR.toLowerCase()}`,
    [Flow.ODR]: process.env.ODR_ENDPOINT || '',
    [`${Flow.ODR}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.ODR.toLowerCase()}`,
    [Flow.EHOCM]: process.env.EHOCM_ENDPOINT || '',
    // Built from FORM_BILLED_APP_ROOT like every other _fr entry. These two used
    // <APP>_ENDPOINT, which already ends in /on-form/<app>, so they produced a doubled
    // path ("/on-form/ehocm/fr/ehocm") instead of "/on-form/fr/ehocm".
    [`${Flow.EHOCM}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.EHOCM.toLowerCase()}`,
    [Flow.OER]: process.env.OER_ENDPOINT || '',
    [`${Flow.OER}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.OER.toLowerCase()}`,
    [Flow.VECON]: process.env.VECON_ENDPOINT || '',
    [`${Flow.VECON}_fr`]: `${process.env.FORM_BILLED_APP_ROOT}/fr/${Flow.VECON.toLowerCase()}`,
} as const;

// Matches the app segment of a form-builder path, e.g. "/on-form/fr/pub-sgr" -> "PUB-SGR".
export const getOnFormAppSegment = (path?: string) =>
    path?.match(/^\/on-form(?:\/fr)?\/([^/?#]+)/i)?.[1]?.toUpperCase();

// CTA copy on the account-creation success screen for form apps that have no Flow
// member, keyed by the /on-form app segment the user is being returned to. A segment
// that is absent here keeps the generic draft label.
export const OnFormContinueLabelKeys: Record<string, string> = {
    'PUB-SGR': 'goToPUB-SGR', // publications service guarantee refund
};

export type SharedUserProfile = {
    userId?: string | null;
    firstName?: string;
    lastName?: string;
    middleName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    extension?: string | null;
    lang: Locale;
    address?: Address;
    dateOfBirth?: string | null;
    healthCard?: HealthCard;
    driverLicence?: DriverLicense;
};

export type FormBuilderAppType = keyof typeof FormBuilderAppUrlMap;
